/**
 * Find a rave in the PHONE APP — the first page somebody sees when they open it (the user,
 * 2026-10-26: "the Find a rave page is the app home page … a few lines: Happening now, This
 * week, This month, Europe, America, etc. — each one scrolls from left to right … a button to
 * see all, then it's the current Find a rave, and a filter by country … this is the master
 * page people will see when opening the app for the first time"). The WEBSITE keeps the list
 * as it was (`Directory`).
 *
 * Rows, in order: Happening now (gates open, not ended) · This week (starting in the next 7
 * days) · This month (8–30 days) · then one row per continent (`continentOf`, @myrave/shared)
 * with every rave there still to come or on, the busiest continent first. A rave may be in a
 * time row AND its continent's. **A row with no rave is not drawn at all — not even its title**
 * (the user). Each row is the app's own `RaveCard`, sideways, snapping card by card.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { continentOf, RAVE_TYPES, type Continent, type RaveType } from '@myrave/shared';
type TFunction = ReturnType<typeof useT>;
import { Button } from '@/ui';
import { useT } from '@/i18n';
import { useLocale } from '@/app/LocaleProvider';
import { intlLocale, rich } from '@/app/rich';
import { RaveCard, type Rave } from './raves';
import './RaveRows.css';

const DAY = 86_400_000;

export interface RaveRow { key: string; title: string; raves: Rave[] }

/**
 * How many ravers a row's raves bring together (the user, 2026-10-26: "if a row has 3 raves of
 * 30k each it shows 90k") — the raves whose crowd is known; null when none is.
 */
export function rowCrowd(raves: readonly Rave[]): number | null {
  const known = raves.map((r) => r.crowdSize).filter((n): n is number => typeof n === 'number' && n > 0);
  return known.length ? known.reduce((a, b) => a + b, 0) : null;
}

const ms = (iso: string | null | undefined) => (iso ? Date.parse(iso) : NaN);

/**
 * The banner over the rows (the user, 2026-10-27: "how many raves in the app, and how many
 * crowded in the active raves — 20M ravers dancing in 400 raves, and it's all here in
 * MyRave.io"): every rave in the app, and the crowds of the ones not over yet.
 */
export function homeTotals(raves: readonly Rave[], now: number): { raves: number; ravers: number | null } {
  return { raves: raves.length, ravers: rowCrowd(raves.filter((r) => !(ms(r.endsAt) < now))) };
}
const soonest = (a: Rave, b: Rave) => (ms(a.gatesOpenAt) || 0) - (ms(b.gatesOpenAt) || 0);

/** The rows, already without the empty ones. Pure — `now` is the caller's. */
/** A type's name in the reader's language — each a literal, as the coverage test reads them. */
export function raveTypeName(tr: TFunction, type: RaveType): string {
  switch (type) {
    case 'psytrance': return tr('Psytrance');
    case 'goa': return tr('Goa');
    case 'progressive': return tr('Progressive');
    case 'techno': return tr('Techno');
    case 'house': return tr('House');
    case 'trance': return tr('Trance');
    case 'drum-and-bass': return tr('Drum & Bass');
    case 'dubstep-and-bass': return tr('Dubstep & Bass');
    case 'hardstyle': return tr('Hardstyle');
    case 'edm': return tr('EDM / Mainstage');
    case 'ambient-and-chill': return tr('Ambient & Chill');
    case 'multi-genre': return tr('Multi-genre');
  }
}

export function raveRows(raves: readonly Rave[], now: number, titles: {
  now: string; week: string; month: string; continent: Record<Continent, string>; type?: (t: RaveType) => string;
  /** The reader's own country (`useLocale().region`) and its row's title — "In Israel" (2026-10-26). */
  home?: { code: string; title: string } | null;
}): RaveRow[] {
  const live = (r: Rave) => ms(r.gatesOpenAt) <= now && now <= ms(r.endsAt);
  const startsWithin = (r: Rave, from: number, to: number) => { const g = ms(r.gatesOpenAt); return g > now + from && g <= now + to; };
  const notOver = (r: Rave) => !(ms(r.endsAt) < now);
  const rows: RaveRow[] = [
    { key: 'now', title: titles.now, raves: raves.filter(live).sort(soonest) },
    { key: 'week', title: titles.week, raves: raves.filter((r) => startsWithin(r, 0, 7 * DAY)).sort(soonest) },
    { key: 'month', title: titles.month, raves: raves.filter((r) => startsWithin(r, 7 * DAY, 30 * DAY)).sort(soonest) },
  ];
  // In my country (the user, 2026-10-26: "add to the cards: in my country") — every rave there still to come or on.
  if (titles.home) {
    const home = titles.home.code.toUpperCase();
    rows.push({ key: 'home', title: titles.home.title, raves: raves.filter((r) => notOver(r) && (r.country ?? '').toUpperCase() === home).sort(soonest) });
  }
  const byContinent = new Map<Continent, Rave[]>();
  for (const r of raves) {
    const c = continentOf(r.country);
    if (!c || !notOver(r)) continue;
    byContinent.set(c, [...(byContinent.get(c) ?? []), r]);
  }
  const continents = [...byContinent.entries()].sort((a, b) => b[1].length - a[1].length);
  for (const [c, list] of continents) rows.push({ key: c, title: titles.continent[c], raves: list.sort(soonest) });
  // Then a row per type (the user, 2026-10-26: "psytrance etc."), the busiest first.
  if (titles.type) {
    // No "Multi-genre" row (the user, 2026-10-27: "remove כמה סגנונות") — it says nothing about the
    // music; it stays a choice in Find a rave's type filter.
    const byType = RAVE_TYPES
      .filter((t) => t !== 'multi-genre')
      .map((t) => ({ t, list: raves.filter((r) => notOver(r) && (r.types ?? []).includes(t)).sort(soonest) }))
      .filter((x) => x.list.length > 0)
      .sort((a, b) => b.list.length - a.list.length);
    for (const { t, list } of byType) rows.push({ key: `type-${t}`, title: titles.type(t), raves: list });
  }
  return rows.filter((row) => row.raves.length > 0);
}

export function RaveRows({ raves, now, onAttend, busyId, onSeeAll, empty, home }: {
  /** The reader's country: a row of its raves ("In Israel"). */
  home?: { code: string; name: string } | null;
  raves: Rave[];
  now: number;
  /** Absent signed out: no Attend button (2026-10-26). */
  onAttend?: (rave: Rave) => void;
  busyId: string | null;
  onSeeAll: () => void;
  /** Nothing in any row. */
  empty: ReactNode;
}) {
  const tr = useT();
  const { locale } = useLocale();
  // "90K" / "90 אלף" — the reader's language, never the browser's (`intlLocale`).
  const compact = useMemo(() => new Intl.NumberFormat(intlLocale(locale), { notation: 'compact', maximumFractionDigits: 1 }), [locale]);
  const rows = useMemo(() => raveRows(raves, now, {
    now: tr('Happening now'), week: tr('This week'), month: tr('This month'),
    continent: {
      europe: tr('Europe'), 'north-america': tr('North America'), 'south-america': tr('South America'),
      'middle-east': tr('Middle East'), asia: tr('Asia'), africa: tr('Africa'), oceania: tr('Oceania'),
    },
    type: (t) => raveTypeName(tr, t),
    home: home ? { code: home.code, title: tr('In {country}', { country: home.name }) } : null,
  }), [raves, now, tr, home]);

  const totals = homeTotals(raves, now);

  return (
    <div className="rave-rows">
      {totals.raves > 0 && totals.ravers !== null && (
        <div className="rave-rows-hero">
          <p className="rave-rows-hero-line">
            {rich(tr('~{ravers} ravers dancing in {raves} raves'), {
              ravers: <b>{compact.format(totals.ravers)}</b>,
              raves: <b>{totals.raves.toLocaleString(intlLocale(locale))}</b>,
            })}
          </p>
          <p className="rave-rows-hero-sub">{tr("And it's all here in MyRave.io")}</p>
        </div>
      )}
      {rows.length === 0 && empty}
      {rows.map((row) => (
        <section key={row.key} className="rave-rows-row" aria-labelledby={`rave-row-${row.key}`}>
          <div className="rave-rows-head">
            <h2 id={`rave-row-${row.key}`} className="rave-rows-title">
              {row.title} <span className="rave-rows-count">{row.raves.length}</span>
            </h2>
            {((crowd) => crowd !== null && (
              <span className="rave-rows-crowd">💃 {tr('~{count} ravers', { count: compact.format(crowd) })}</span>
            ))(rowCrowd(row.raves))}
          </div>
          <RowStrip>
            {row.raves.map((r) => (
              <li key={r.editionId} className="rave-rows-item">
                <RaveCard rave={r} now={now} onAttend={onAttend ? () => onAttend(r) : undefined} busy={busyId === r.editionId} showCrowd />
              </li>
            ))}
          </RowStrip>
        </section>
      ))}
      <div className="rave-rows-all">
        <Button variant="secondary" full onClick={onSeeAll}>{tr('See all raves')}</Button>
      </div>
    </div>
  );
}

/**
 * One row's strip — swiped on a phone; on a computer, with arrows at its two ends too (the user,
 * 2026-10-27: "on web we need also a scroller left to right, maybe arrows"). An arrow scrolls
 * most of a screen of cards and is greyed at its end; in a right-to-left language "next" is the
 * left one. The arrows show only where there is a mouse (`RaveRows.css`).
 */
function RowStrip({ children }: { children: ReactNode }) {
  const tr = useT();
  const { direction } = useLocale();
  const strip = useRef<HTMLUListElement>(null);
  const [ends, setEnds] = useState({ start: true, end: true });
  useEffect(() => {
    const el = strip.current;
    if (!el) return undefined;
    const measure = () => {
      // `scrollLeft` runs negative in a right-to-left strip: the distance from the start is its size.
      const gone = Math.abs(el.scrollLeft);
      setEnds({ start: gone <= 2, end: gone + el.clientWidth >= el.scrollWidth - 2 });
    };
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => { el.removeEventListener('scroll', measure); window.removeEventListener('resize', measure); };
  }, [children]);
  const move = (forward: boolean) => {
    const el = strip.current;
    if (!el) return;
    const step = Math.max(200, el.clientWidth * 0.85) * (forward ? 1 : -1) * (direction === 'rtl' ? -1 : 1);
    el.scrollBy({ left: step, behavior: 'smooth' });
  };
  return (
    <div className="rave-rows-strip-wrap">
      <ul ref={strip} className="rave-rows-strip" data-no-pull>{children}</ul>
      {!(ends.start && ends.end) && (
        <>
          <button type="button" className="rave-rows-arrow" data-side="start" disabled={ends.start} aria-label={tr('Previous')} onClick={() => move(false)}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
          </button>
          <button type="button" className="rave-rows-arrow" data-side="end" disabled={ends.end} aria-label={tr('Next')} onClick={() => move(true)}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 5l7 7-7 7" /></svg>
          </button>
        </>
      )}
    </div>
  );
}
